import { Button, Card, Space, Table } from "@arco-design/web-react";
import { Currency } from "@/components/Numeric";
import CustomerSelect from "@/components/CustomerSelect";
import { useCallback, useEffect, useState } from "react";
import { SOAPI } from "@/apis/sales";
import { FileUtil } from "@/utils";
import { SaleOrderInfo } from "./so-info";
import { SOAdd } from "./so-add";
import { SOEdit } from "./so-edit";
import SaleOrderLink from "@/components/SaleOrderLink";
import Reference from "@/components/Reference";
import { useNavigate } from "react-router-dom";
import { SOStatusDef, SOStatus } from "@/enums/sales";
import { buildEnumOptions } from "@/enums/utils";
import { EnumLabel, EnumSelect } from "@/components/Enum";

const OPEN_STATUSES = [
    SOStatus.Created,
    SOStatus.Issued,
    SOStatus.Picked,
    SOStatus.Partial,
    SOStatus.PartialFulfilled,
    SOStatus.PartialFulfilledInvoiced,
    SOStatus.Fulfilled,
    SOStatus.FulfilledPartiallyInvoiced,
    SOStatus.PartiallyInvoiced
];

const CLOSED_STATUSES = [
    SOStatus.ShortClosed,
    SOStatus.FulfilledInvoiced,
    SOStatus.Voided
];

const STATUS_OPTIONS = [
    { value: "open", label: "Open" },
    { value: "closed", label: "Closed" },
    ...buildEnumOptions(SOStatusDef)
];

const expandStatus = (status) => {
    if (!status) return null;
    if (status === "open") return OPEN_STATUSES;
    if (status === "closed") return CLOSED_STATUSES;
    return [status];
};

export const SaleOrder = () => {
    const [status, setStatus] = useState("open");
    const [customerId, setCustomerId] = useState(null);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);
    const navigate = useNavigate();

    const getSOs = useCallback(async () => {
        const values = await SOAPI.query(page, { status: expandStatus(status), customers: customerId });
        setData(values.records);
        setTotal(values.total);
    }, [status, customerId, page]);

    const exportSOs = useCallback(async () => {
        setExporting(true);
        try {
            const res = await SOAPI.export({
                status: expandStatus(status),
                customers: customerId ? [customerId] : undefined
            });
            FileUtil.download(res.data, "Sale Orders.xlsx");
        } finally {
            setExporting(false);
        }
    }, [status, customerId]);

    useEffect(() => {
        getSOs();
    }, [getSOs]);

    return (
        <Card title="Sales Order" extra={
            <Space>
                <CustomerSelect allowClear placeholder="Select customer" style={{ width: 200 }} onChange={(val) => {
                    setCustomerId(val);
                }} />
                <EnumSelect options={STATUS_OPTIONS} allowAll placeholder="Status" style={{ width: 200 }}
                    value={status} onChange={setStatus} />
                <Button type="primary" onClick={() => navigate("/workspace/sales/so/add")}>Create New SO</Button>
                <Button type="primary" loading={exporting} onClick={exportSOs}>Export</Button>
            </Space>
        }>
            <Table rowKey="id"
                columns={[
                    {
                        title: "SO#",
                        render: (col, record) =>
                            <SaleOrderLink soId={record.id}>{record.soNo}</SaleOrderLink>
                    },
                    { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={SOStatusDef} value={record.status} /> },
                    { title: "Customer", dataIndex: "customerName" },
                    { title: "Reference#", render: (col, record) => <Reference value={record.reference} /> },
                    { title: "SO Date", dataIndex: "soDate" },
                    { title: "Due Date", dataIndex: "dueDate" },
                    { title: "Total Amount", render: (col, record) => <Currency value={record.totalAmount} /> },
                ]}
                pagination={{ total, current: page, pageSize: 20 }}
                data={data}
                onChange={(pagination) => setPage(pagination.current)}
            />
        </Card>
    );
}

SaleOrder.Info = SaleOrderInfo;
SaleOrder.Add = SOAdd;
SaleOrder.Edit = SOEdit;
